import React from 'react';
import { motion } from 'framer-motion';
import {
    Sprout,
    Wind,
    ArrowRight,
    ChevronRight,
    Feather,
    Mail,
    Linkedin
} from 'lucide-react';
import hamudurwo from '../assets/hamuduruwo.jpeg';
import drmanike from '../assets/drmanike.jpeg';
import ketakirilla from '../assets/ketakirilla.jpg';
import logo from '../assets/logo.png';
import mama from '../assets/mama.png';
import kumari from '../assets/kumarinanda.png';
import madushani from '../assets/madushani.png';
import hero from '../assets/aboutus.jpg';


import maduakka from '../assets/maduakka.jpeg';

export default function AboutUs() {
    // BRAND COLORS
    const darkGreen = "#133319";
    const goldAccent = "#D4AF37";
    const creamBg = "#FDFBF7";

    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";
    const heroImage = hero;

    const fadeIn = {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: "-50px" },
        transition: { duration: 0.8, ease: "easeOut" }
    };

    // 3 DIRECTORS
    const directors = [
        { name: "Mrs. Sunethra Senadhipathi", role: "", image: drmanike },
        { name: "Mr. Sarath Senadipathi", role: "", image: mama },
        { name: "Mrs. Kumari Gunathilaka", role: "", image: kumari }
    ];

    // 3 MEMBERS
    const members = [
        { name: "Ven. Gampaha Samiddhi Thero", role: "", image: hamudurwo },
        { name: "Mrs. Madushani Prabodha", role: "", image: madushani },
        { name: "Mrs. Maduka Kalpanie Silva", role: "", image: maduakka }
    ];

    return (
        <div style={{ backgroundColor: creamBg, color: darkGreen }} className="min-h-screen font-sans selection:bg-[#D4AF37] selection:text-white relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            {/* HERO SECTION */}
            <section className="relative h-[60vh] md:h-[65vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img src={heroImage} className="w-full h-full object-cover" alt="Boseth Heritage" />
                    <div className="absolute inset-0" style={{ backgroundColor: `${darkGreen}66` }}></div>
                </div>
                <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-20">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                        <div className="flex items-center gap-2 text-white/90 text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.4em] uppercase mb-4 sm:mb-6 font-bold">
                            <span>Home</span>
                            <ChevronRight size={12} style={{ color: goldAccent }} />
                            <span className="text-white font-medium">About Our Foundation</span>
                        </div>
                        <h1 className="font-serif text-4xl sm:text-6xl md:text-8xl text-white leading-tight">
                            The Soul of <br/>
                            <span className="italic font-light" style={{ color: goldAccent }}>Boseth Wellness</span>
                        </h1>
                        <p className="mt-6 sm:mt-8 text-white/80 text-xs sm:text-base md:text-xl font-light tracking-widest uppercase max-w-2xl">
                            Dedicated physicians, staff, and volunteers preserving traditional wellness.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* VISION & MISSION CARDS */}
            <section className="relative -mt-16 sm:-mt-20 z-30 px-4 sm:px-6 pb-20 sm:pb-24">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-0 shadow-2xl rounded-[2rem] sm:rounded-[3rem] overflow-hidden">
                        <motion.div {...fadeIn} className="p-8 sm:p-12 md:p-20 flex flex-col justify-center text-white" style={{ backgroundColor: darkGreen }}>
                            <div className="h-10 w-10 sm:h-12 w-12 rounded-full border flex items-center justify-center mb-6 sm:mb-8" style={{ borderColor: `${goldAccent}4D` }}>
                                <Wind color={goldAccent} size={20} className="sm:w-6 sm:h-6" />
                            </div>
                            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl mb-4 sm:mb-6">Our Vision</h2>
                            <p className="text-gray-300 text-sm sm:text-base md:text-lg font-light leading-relaxed">
                                A sanctuary where ancient wisdom heals the body, calms the mind, and awakens the spirit, nurturing the path to inner freedom and spiritual restoration.
                            </p>
                        </motion.div>
                        <motion.div {...fadeIn} className="bg-white/95 backdrop-blur-md p-8 sm:p-12 md:p-20 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-[#E5E0D8]">
                            <div className="h-10 w-10 sm:h-12 w-12 rounded-full border flex items-center justify-center mb-6 sm:mb-8" style={{ borderColor: `${goldAccent}4D` }}>
                                <Sprout color={goldAccent} size={20} className="sm:w-6 sm:h-6" />
                            </div>
                            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl mb-4 sm:mb-6" style={{ color: darkGreen }}>Our Mission</h2>
                            <p className="text-gray-600 text-sm sm:text-base md:text-lg font-light leading-relaxed">
                                To preserve and protect the healing knowledge cherished through generations of Hela Wedakama and share this wisdom globally for holistic health.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* LOGO STORY */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <motion.div {...fadeIn} className="relative order-2 lg:order-1 flex justify-center">
                            {/* POLAROID STYLE */}
                            <div className="bg-white rounded-3xl p-4 sm:p-6 pb-16 sm:pb-24 shadow-2xl border border-gray-100 -rotate-2 w-full max-w-sm md:max-w-md">
                                <img
                                    src={ketakirilla}
                                    alt="Boseth Logo"
                                    className="w-full h-full object-contain"
                                />
                            </div>

                            {/* LABEL */}
                            <div className="absolute -bottom-4 -right-2 sm:right-4 text-white p-4 sm:p-6 rounded-2xl shadow-xl -rotate-2 z-20" style={{ backgroundColor: goldAccent }}>
                                <Feather size={18} className="sm:w-5 sm:h-5" />
                                <p className="mt-1 font-serif text-base sm:text-lg italic">“Keta Kirilla”</p>
                            </div>
                        </motion.div>
                        <motion.div {...fadeIn} className="space-y-4 sm:space-y-8 order-1 lg:order-2 px-2 sm:px-4">
                            <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block">The Story of Our Identity</span>
                            <h2 className="font-serif text-3xl sm:text-5xl md:text-7xl leading-tight" style={{ color: darkGreen }}>
                                The Sacred <br />
                                <span className="italic font-light" style={{ color: goldAccent }}>Messenger</span>
                            </h2>
                            <p className="text-gray-600 text-base sm:text-lg md:text-xl font-light leading-relaxed">
                                Our logo features the **"Keta Kirilla,"** a small bird that symbolizes the delicate balance of nature and serves as the messenger of peace and healing in ancient lore.
                            </p>
                            <div className="pt-4 sm:pt-6">
                                <p className="font-serif text-lg sm:text-2xl italic border-l-4 pl-4 sm:pl-8 py-3 sm:py-4 rounded-r-2xl" style={{ color: darkGreen, borderColor: goldAccent, backgroundColor: `${goldAccent}0D` }}>
                                    "Explore, Engage, Grow with Boseth Wellness Foundation"
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* LEADERSHIP & TEAM SECTION */}
            <section className="relative py-20 sm:py-32 bg-[#0B1A10]/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">

                    {/* DIRECTORS SUB-SECTION */}
                    <div className="text-center mb-12 sm:mb-16">
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block mb-2 sm:mb-4">Leadership</span>
                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl" style={{ color: darkGreen }}>Board of Directors</h2>
                        <div className="h-1 w-16 sm:w-20 mx-auto mt-4 sm:mt-6" style={{ backgroundColor: goldAccent }}></div>
                    </div>

                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-8 lg:gap-12 mb-16 sm:mb-28 max-w-5xl mx-auto">
                        {directors.map((dir, i) => (
                            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }} className="group text-center">
                                <div className="relative aspect-[4/5] max-w-[290px] sm:max-w-[340px] md:max-w-full mx-auto overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] mb-4 sm:mb-8 shadow-2xl">
                                    <img src={dir.image} alt={dir.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                    <div className="absolute inset-0 via-transparent to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-500 flex items-end justify-center pb-6 sm:pb-8" style={{ background: `linear-gradient(to top, ${darkGreen}, transparent)` }}>
                                        <div className="flex gap-4">
                                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer"><Mail size={14} className="text-white sm:w-4 sm:h-4" /></div>
                                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer"><Linkedin size={14} className="text-white sm:w-4 sm:h-4" /></div>
                                        </div>
                                    </div>
                                </div>
                                <h4 className="font-serif text-xl sm:text-2xl mb-1 sm:mb-2 max-w-[280px] mx-auto" style={{ color: darkGreen }}>{dir.name}</h4>
                                <p style={{ color: goldAccent }} className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2.5em] sm:tracking-[0.3em]">{dir.role}</p>
                            </motion.div>
                        ))}
                    </div>

                    {/* MEMBERS SUB-SECTION */}
                    <div className="text-center mb-12 sm:mb-16 pt-12 sm:pt-16 border-t border-gray-200/60">
                        <span style={{ color: goldAccent }} className="font-bold uppercase tracking-[0.3em] sm:tracking-[0.4em] text-[10px] sm:text-[11px] block mb-2 sm:mb-4">Our Team</span>
                        <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl" style={{ color: darkGreen }}>Foundation Members</h2>
                        <div className="h-1 w-16 sm:w-20 mx-auto mt-4 sm:mt-6" style={{ backgroundColor: goldAccent }}></div>
                    </div>

                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-8 lg:gap-12 max-w-5xl mx-auto">
                        {members.map((member, i) => (
                            <motion.div key={i} {...fadeIn} transition={{ delay: i * 0.1 }} className="group text-center">
                                <div className="relative aspect-[4/5] max-w-[290px] sm:max-w-[340px] md:max-w-full mx-auto overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] mb-4 sm:mb-8 shadow-2xl">
                                    <img src={member.image} alt={member.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                    <div className="absolute inset-0 via-transparent to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-500 flex items-end justify-center pb-6 sm:pb-8" style={{ background: `linear-gradient(to top, ${darkGreen}, transparent)` }}>
                                        <div className="flex gap-4">
                                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer"><Mail size={14} className="text-white sm:w-4 sm:h-4" /></div>
                                            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#D4AF37] transition-colors cursor-pointer"><Linkedin size={14} className="text-white sm:w-4 sm:h-4" /></div>
                                        </div>
                                    </div>
                                </div>
                                <h4 className="font-serif text-xl sm:text-2xl mb-1 sm:mb-2 max-w-[280px] mx-auto" style={{ color: darkGreen }}>{member.name}</h4>
                                <p style={{ color: goldAccent }} className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2.5em] sm:tracking-[0.3em]">{member.role}</p>
                            </motion.div>
                        ))}
                    </div>

                </div>
            </section>

            {/* CALL TO ACTION */}
            <section className="relative py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">
                    <motion.div {...fadeIn} className="rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-12 md:p-16 flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-10 shadow-2xl relative overflow-hidden text-white" style={{ backgroundColor: darkGreen }}>
                        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: `url(${bgPattern})` }}></div>
                        <div className="relative z-10 text-center lg:text-left">
                            <h2 className="font-serif text-2xl sm:text-4xl md:text-5xl mb-3 sm:mb-4">Ready to begin your journey?</h2>
                            <p className="text-white/60 text-sm sm:text-base md:text-lg font-light max-w-xl">Consult with our traditional physicians today. Your wellness journey supports our mission to preserve Hela Wedakama.</p>
                        </div>
                        <button style={{ backgroundColor: goldAccent }} className="relative z-10 text-white px-8 sm:px-10 py-4 sm:py-5 rounded-full font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.2em] hover:bg-white hover:text-[#0B1A10] transition-all duration-500 flex items-center gap-3 shadow-xl group whitespace-nowrap">
                            Book a Consultation <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}